import {
  CircularProgress,
  Grid,
  Typography,
  MenuItem,
  Divider,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  InputAdornment,
  IconButton,
  Stack,
  Tooltip
} from '@mui/material'

import React, { useEffect, useRef, useState } from 'react'

import { apiGet, apiPut } from '../../generic/Api_Functions'
import { useNavigate, generatePath } from 'react-router'
import ButtonBackSave from '../../generic/buttons/floatingButtons/ButtonBackSave'
import { PositionCard } from '../../generic/utilities/PositionCard'
import { DefaultAccordion } from '../../generic/DefaultAccordion'
import { TextFieldWithUnit } from '../../generic/utilities/TextFieldWithUnit'
import { SmartSaveDialog, SmartDialog } from '../../generic/utilities/SmartDialog'
import createDialogTitle from '../../generic/utilities/dialogUtil'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { systemValueLabel, systemValueOptionText } from '../../generic/systemValues/systemValueLabel'
import { SmartSelect } from '../../generic/smartSelect'
import { DatePicker } from '@mui/x-date-pickers'
import { useAuthorization } from '../../generic/hooks/useAuthorization'
import { useDateFormatter } from '../../generic/hooks/useDateFormatter'
import AlertUI from '../../generic/AlertUI'

const DeviceConfiguration = (props) => {
  const { deviceId, device, setDevice } = props
  const [loaded, setLoaded] = useState(false)
  const [deviceMode, setDeviceMode] = useState([])
  const [protocols, setProtocols] = useState([])
  const [technologies, setTechnologies] = useState([])
  const [toolholderStandards, setToolholderStandards] = useState([])
  const [deviceParents, setDeviceParents] = useState([])
  const [storageTypes, setStorageTypes] = useState([])
  const backSaveButtonRef = useRef()
  const [editMode, setEditMode] = useState(false)
  const [parameterIsInError, setParameterIsInError] = useState({})
  const [showSaveConfirm, setShowSaveConfirm] = useState(false)
  const [showSaveErrorModal, setShowSaveErrorModal] = useState(false)
  const [isMTC, setIsMTC] = useState(device?.storageTypes?.at(0)?.code === 'MTC')
  const [showError, setShowError] = useState(null)
  const [errorMessage, setErrorMessage] = useState('Something went wrong')
  const [storage, setStorage] = useState(null)
  const { t_ } = useSmartTranslation()
  const isUserAuthorized = useAuthorization()
  const { format } = useDateFormatter()
  const [AlertElement, showAlert] = AlertUI()
  const navigate = useNavigate()
  const [calendarName, setCalendarName] = useState(t_('Calendar'))

  useEffect(() => {
    const fetchData = async () => {
      const deviceModeDB = await apiGet('devices/mode')
      const protocolsDB = await apiGet('protocols')
      // A device can come without parameter values (e.g. one created by a migration): no toolholder standard
      // parameter means no standards to offer, not a crash.
      const toolholderStandardUnitTypeId = device.parameterList?.find(parameter => parameter.code === 'TOOLHOLDER_STANDARD')?.unitType?.id
      const toolHolderStandardsDB = toolholderStandardUnitTypeId === undefined
        ? []
        : await apiGet('unitTypeValuesByUnitType/' + toolholderStandardUnitTypeId)
      const deviceParentsDB = await apiGet('devices/deviceParents/' + deviceId)
      const storageTypesDB = await apiGet('storageTypes')
      setDeviceMode(deviceModeDB)
      setProtocols(protocolsDB)
      setToolholderStandards(toolHolderStandardsDB)
      setTechnologies(device.deviceType.technologyDeviceTypes.map(a => a.technology))
      setDeviceParents(deviceParentsDB)
      setStorageTypes(storageTypesDB)
    }
    if (!loaded) {
      fetchData()
      setLoaded(true)
    }
  }, [loaded, deviceId, device])

  useEffect(() => {
    if (!device?.calendarId) return

    const fetchCalendarName = async () => {
      try {
        const resp = await apiGet('calendar/' + device.calendarId)

        const rawName = resp?.calendarReference?.name
        const finalName =
          (typeof rawName === 'string' && rawName.trim().length > 0)
            ? rawName.trim()
            : t_('Calendar')

        setCalendarName(finalName)
      } catch (err) {
        console.error('Error al consultar calendar:', err)
        setCalendarName(t_('Calendar'))
      }
    }

    fetchCalendarName()
  }, [device?.calendarId, t_])

  // Absent means false (frozen contract 03-dominio.md 5.1): an asset that Sun has not marked yet is
  // internal, so the form must not read an absent field as anything else.
  const isExternalDevice = device.external === true

  const changeBetweenVisualizeModes = (edit) => {
    setEditMode(edit)
  }

  const formatDeviceToPut = (deviceCopy) => {
    const dev = Object.assign(deviceCopy)
    dev.deviceType = {
      name: device.deviceType.name
    }
    return dev
  }

  const handleValue = (deviceTypeFunction, protocol) => {
    const deviceFunctionProtocol = device.deviceFunctionProtocols.find(
      (deviceFunctionProtocol) =>
        deviceFunctionProtocol.functionProtocol.function.id ===
        deviceTypeFunction.function.id
    )

    const functionProtocolConfiguration = deviceFunctionProtocol.functionProtocol.functionProtocolConfigurations.find(
      (configured) => {
        if (configured.protocolParameter.id ===
          protocol.id) {
          return configured.protocolValue
        } else return ''
      })
    if (functionProtocolConfiguration !== undefined) {
      return functionProtocolConfiguration.protocolValue
    }
    return ''
  }

  const goBack = (state) => {
    navigate('/configuration/assets', { replace: true, state })
  }

  const updateDevice = () => {
    const deviceCopy = { ...device }

    if (isMTC) {
      const containerTech = technologies.find(t => t.code === 'CONTAINER')
      if (containerTech) {
        deviceCopy.deviceTechnologies = [{
          device: { id: device.id, name: device.name },
          technology: containerTech
        }]
      }
    }

    apiPut('devices/config', formatDeviceToPut(deviceCopy))
      .then((data) => {
        setDevice(data)
        if (device.deviceType.name !== 'STORAGE' || device.deviceTechnologies > 1 || device.deviceTechnologies[0] === undefined) {
          goBack('EDIT')
        } else {
          apiPut('shelveStorageConfigurationsUpdateAllTechnology', device.deviceTechnologies[0]).then((data) => {
            setDevice(data)
            goBack('EDIT')
          })
            .catch((error) => {
              backSaveButtonRef.current.showErrorAlert({
                title: 'An error occurred while editing.',
                message: error.message,
                error: 'error'
              })
            })
        }
      })
      .catch((error) => {
        showAlert({
          title: '',
          message: error.message,
          severity: 'error'
        })
        backSaveButtonRef.current.showErrorAlert({
          title: 'An error occurred while editing.',
          message: error.message,
          error: 'error'
        })
      })
    setShowSaveConfirm(false)
  }

  const saveDevice = () => {
    if (Object.values(parameterIsInError).find(parameter => parameter === true) === undefined && !showError) { setShowSaveConfirm(true) } else {
      setShowSaveErrorModal(true)
    }
  }

  const selectedTechnologiesMultiSelect = (device) => {
    const returnValue = []

    if (device.deviceTechnologies === null || device.deviceTechnologies === undefined) {
      return returnValue
    } else if (Object.keys(device.deviceTechnologies).length === 0) {
      return returnValue
    } else if (device.deviceTechnologies !== undefined && device.deviceTechnologies !== null && Array.isArray(device.deviceTechnologies)) {
      let selectedValues = []
      device.deviceTechnologies.forEach((opt) => {
        selectedValues = selectedValues.concat(opt.technology.name)
      })
      return selectedValues
    } else {
      return returnValue
    }
  }

  const optionTechnologiesMultiSelect = () => {
    if (technologies === null || technologies === undefined) {
      return []
    } else if (Object.keys(technologies).length === 0) {
      return []
    } else if (technologies !== undefined && technologies !== null && Array.isArray(technologies)) {
      const selectedValues = technologies.map((opt) => {
        return opt.name
      })
      return selectedValues
    } else {
      return []
    }
  }

  const onChangeTechnologyValues = (newTechnologiesSelectedValues, device) => {
    let newValues = []
    newTechnologiesSelectedValues.forEach((value) => {
      const newValue = technologies.find(elem => elem.name === value)
      const deviceTechnology = {
        device: { id: device.id, name: device.name }, technology: newValue
      }
      newValues = newValues.concat([deviceTechnology])
    })
    setDevice(({ ...device, deviceTechnologies: newValues }))
  }

  const handleChangeTool = (event) => {
    const copyDevice = { ...device }
    copyDevice.toolChangeEnabled = event.target.checked
    setDevice(copyDevice)
  }

  const selectableOptionsStorageTypes = () => {
    if (storageTypes === null || storageTypes === undefined) {
      return []
    } else if (Object.keys(storageTypes).length === 0) {
      return []
    } else if (storageTypes !== undefined && storageTypes !== null && Array.isArray(storageTypes)) {
      const selectedValues = storageTypes.map((opt) => {
        return opt.name
      })
      return selectedValues
    } else {
      return []
    }
  }

  const onChangeStorageType = (newStorageTypesSelectedValue) => {
    const selectedType = storageTypes.find(elem => elem.name === newStorageTypesSelectedValue)
    if (selectedType === undefined) {
      setDevice({ ...device, storageTypes: [] })
    }
    if (selectedType?.code === 'MTC') {
      const shelfs = storage?.shelveStorageConfigurations?.length
      const rows = storage?.shelveStorageConfigurations?.at(0)?.rows

      setIsMTC(true)
      if (shelfs > 1) {
        setShowError(true)
        setErrorMessage('MTC storage type only supports one shelf')
      }
      if (rows > 1) {
        setShowError(true)
        setErrorMessage('MTC storage type only supports one row')
      }
    } else {
      setIsMTC(false)
      setShowError(false)
    }
    if (selectedType) {
      setDevice({ ...device, storageTypes: [selectedType] })
    }
  }

  useEffect(() => {
    if (device.deviceType.name === 'STORAGE') {
      const fetchStorage = async () => {
        const st = await apiGet('storages/byDeviceId/' + deviceId)
        setStorage(st)
      }
      fetchStorage()
    }
  }, [device.storageTypes, deviceId, device.deviceType.name])

  const handleChangeDeviceParent = (event) => {
    if (event === null) {
      setDevice({
        ...device,
        parent: null
      })
      return
    }
    const parent = deviceParents.find(
      (item) => item.name === event
    )
    setDevice({
      ...device,
      parent
    })
  }

  const onPositionChange = (key, newValue) => {
    setDevice({ ...device, position: { ...device.position, [key]: newValue } })
  }

  const onParameterChange = (newValue, parameterName) => {
    const parameterAux = device.parameterList
    parameterAux.find(parameter => parameter.name === parameterName).value = newValue
    setDevice({ ...device, parameterList: parameterAux })
  }

  const onParameterUnitChange = (newValue, parameterName) => {
    const parameterListAux = device.parameterList
    const parameter = parameterListAux.find(parameter => parameter.name === parameterName)
    parameter.unit = parameter.unitType.availableUnits.find(unit => unit.code === newValue)
    setDevice({ ...device, parameterList: parameterListAux })
  }

  const onParameterErrorStateChange = (newValue, parameterName) => {
    const parameterIsInErrorAux = parameterIsInError
    parameterIsInErrorAux[parameterName] = newValue
    setParameterIsInError(parameterIsInErrorAux)
  }

  const printDevice = () => {
    return (
      <div>
        <SmartSaveDialog
          show={showSaveConfirm}
          cancelCallback={() => { setShowSaveConfirm(false) }}
          saveCallback={updateDevice}
        />
        <SmartDialog
          setOpen={showSaveErrorModal}
          title={createDialogTitle({ color: 'error', title: 'Error saving', icon: WarningAmberIcon })}
          message={showError ? t_(errorMessage) : 'There is a parameter without a unit assigned'}
          cancel={false}
          accept={false}
          close
          closeCallback={() => { setShowSaveErrorModal(false) }}

        />
        {AlertElement}
        <DefaultAccordion title={t_('GENERAL PROPERTIES')}>
          <Grid container direction='row' spacing={2} columns={{ xs: 1, sm: 4, md: 8, lg: 12 }}>
            <Grid item xs={2}>
              <TextField
                fullWidth
                label={t_('Name')}
                id='deviceName'
                value={device.name}
                onChange={(event) => {
                  setDevice({ ...device, name: event.target.value })
                }}
                disabled={!editMode}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label={t_('Description')}
                id='description'
                value={device.description}
                onChange={(event) => {
                  setDevice({ ...device, description: event.target.value })
                }}
                disabled={!editMode}
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                fullWidth
                id='type'
                label={t_('Type')}
                value={systemValueLabel(t_, 'DeviceTypeEnum', device?.deviceType?.name ?? '')}
                disabled
              />
            </Grid>
            <Grid item xs={1}>
              <TextField
                fullWidth
                id='presetAngle'
                label={t_('presetAngle')}
                type='number'
                value={device.presetAngle ? device.presetAngle : 0}
                onChange={(event) => {
                  setDevice({ ...device, presetAngle: event.target.value })
                }}
                disabled={!editMode}
              />
            </Grid>
            <Grid item xs={1}>
              <TextField id='status' label={t_('Status')} value={device.status} disabled />
            </Grid>
            <Grid item xs={1}>
              <TextField
                fullWidth
                select
                id='micStatus'
                label={t_('Mode')}
                value={deviceMode.length > 0 ? device.mode : ''}
                onChange={(event) => {
                  const mode = event.target.value
                  const status = mode === 'OFFLINE' ? 'IDLE' : 'OFF'
                  setDevice({ ...device, mode, status })
                }}
                disabled={!editMode}
              >
                {deviceMode.map((mode, index) => (
                  <MenuItem key={mode + index} value={mode} disabled={mode === 'NON_OPERATIVE'}>
                    {systemValueLabel(t_, 'DeviceModeEnum', mode)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={2}>
              <TextField
                label={t_('Electrical consumption')}
                type='number'
                value={device.energyConsumption}
                onChange={(event) => {
                  const value = event.target.value
                  if (value === '') {
                    setDevice({ ...device, energyConsumption: '' })
                    return
                  }
                  const num = parseFloat(value)
                  if (!isNaN(num) && num >= 0) {
                    setDevice({ ...device, energyConsumption: value })
                  }
                }}
                InputLabelProps={{ shrink: true }}
                disabled={!editMode}
                InputProps={{
                  endAdornment: <InputAdornment position='end'>kWh</InputAdornment>,
                  inputProps: { min: 0, step: 0.01 },
                  readOnly: !editMode
                }}
              />

            </Grid>
          </Grid>
          <Grid container direction='row' spacing={3} columns={{ xs: 1, sm: 4, md: 8, lg: 12 }} sx={{ paddingTop: '2em' }}>
            <Grid item xs={2}>
              <TextField
                fullWidth
                select
                id='driver'
                label={t_('Driver')}
                value={
                  device.deviceType.drivers.length > 0
                    ? device.driver
                      ? device.driver.id
                      : 0
                    : 0
                }
                onChange={(event) => {
                  setDevice({
                    ...device,
                    driver: device.deviceType.drivers.find(
                      (driver) => driver.id === event.target.value
                    )
                  })
                }}
                disabled={!editMode || !isUserAuthorized('PROPERTIES_DEVICE_DRIVER')}
              >
                <MenuItem key={0} value={0}>
                  {t_('NONE')}
                </MenuItem>
                {device.deviceType.drivers.map((driver, index) => (
                  <MenuItem key={driver.id + index} value={driver.id}>
                    {driver.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={2}>
              <SmartSelect
                disabled={!editMode || !isUserAuthorized('PROPERTIES_DEVICE_TECHNOLOGIES')}
                selectableOptions={optionTechnologiesMultiSelect(device)}
                value={selectedTechnologiesMultiSelect(device)}
                multiple
                onChange={(newValues) => onChangeTechnologyValues(newValues, device)}
                label={t_('Technologies')}
                readOnly={device.deviceType.name === 'STORAGE'}
              />
            </Grid>
            <Grid item xs={1}>
              {device.deviceType.name === 'STORAGE'
                ? (
                  <TextField
                    disabled
                    value={device.morphology ? systemValueLabel(t_, 'morphology', { code: String(device.morphology).toUpperCase(), name: device.morphology }) : ''}
                    label={t_('Morphology')}
                    InputLabelProps={{
                      shrink: true
                    }}
                  />
                  )
                : null}
            </Grid>
            <Grid item xs={1}>
              {device.deviceType.name === 'STORAGE'
                ? (
                  <>
                    <SmartSelect
                      label={t_('Storage type')}
                      disabled={!editMode}
                      value={device.storageTypes?.at(0)?.name}
                      onChange={onChangeStorageType}
                      selectableOptions={selectableOptionsStorageTypes()}
                      optionLabelTransform={(name) => systemValueOptionText(t_, 'storage_type', storageTypes, name)}
                      error={showError}
                      disableClearable={false}
                    />
                  </>
                  )
                : <FormControlLabel
                    control={
                      <Checkbox
                        checked={device.toolChangeEnabled}
                        onChange={handleChangeTool}
                        disabled={!editMode}
                      />
                  } label={t_('Tool change')}
                  />}
            </Grid>
            <Grid item xs={2}>
              {deviceParents?.length !== 0
                ? (
                  <SmartSelect
                    disabled={!editMode}
                    value={device.parent?.name}
                    selectableOptions={deviceParents.map((item) => item.name)}
                    label={t_('Parent asset')}
                    onChange={handleChangeDeviceParent}
                    disableClearable={false}
                    autoSelectFirst={false}
                  />
                  )
                : <TextField
                    disabled
                    value=''
                    label={t_('Parent asset')}
                  />}
            </Grid>
            <Grid item xs={1.7}>
              <SmartSelect
                disabled={!editMode}
                value={t_(device.forecast)?.toUpperCase()}
                selectableOptions={[t_('true').toUpperCase(), t_('false').toUpperCase()]}
                label={t_('Forecast')}
                onChange={(newValue) => {
                  if (t_('true').toUpperCase() === newValue) {
                    setDevice({ ...device, forecast: true })
                    return
                  }
                  setDevice({ ...device, forecast: false })
                }}
              />
            </Grid>
            <Grid item xs={1.7}>
              <Stack direction='row' spacing={1} alignItems='center'>
                <SmartSelect
                  disabled={!editMode}
                  value={isExternalDevice ? t_('true').toUpperCase() : t_('false').toUpperCase()}
                  selectableOptions={[t_('true').toUpperCase(), t_('false').toUpperCase()]}
                  label={t_('External')}
                  onChange={(newValue) => {
                    setDevice({ ...device, external: t_('true').toUpperCase() === newValue })
                  }}
                />
                {/* RN1/D27: the mark changes how the planner reads this asset (infinite capacity and no
                    shift calendar), so the form explains it right where it is chosen. `describeChild`
                    keeps the icon's own label as its accessible name and exposes the explanation as its
                    description, instead of overwriting the name with the whole sentence. */}
                <Tooltip describeChild title={t_('Subcontracting asset: infinite capacity and no shift calendar')}>
                  <IconButton aria-label={t_('External asset')} size='small' disableRipple>
                    <InfoOutlinedIcon fontSize='small' color='primary' />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Grid>
            <Grid item xs={2.3}>
              <DatePicker
                label={t_('Entry date')}
                inputFormat={format.date}
                value={device.entryDate ? new Date(device.entryDate) : null}
                onChange={(newValue) => {
                  setDevice({ ...device, entryDate: newValue })
                }}
                disabled={!editMode}
                slotProps={{
                  field: { clearable: true }
                }}
              />
            </Grid>
            <Grid item xs={2}>
              {/* RN7.2/D27: an external asset is planned in natural time and its shift calendar is
                  never read, so reaching it from here would only invite "I gave it shifts and it
                  ignores them". The calendar itself is kept (RN7.1): this disables the access, it
                  does not erase the assignment. The tooltip wraps a span because a disabled MUI
                  button receives no pointer events. */}
              <Tooltip title={isExternalDevice ? t_('An external asset has no shift calendar') : ''}>
                <span>
                  <Button
                    variant='outlined'
                    disabled={isExternalDevice || editMode || !isUserAuthorized('PROPERTIES_DEVICE_CALENDAR')}
                    endIcon={<CalendarMonthIcon />}
                    onClick={() => navigate(generatePath('/configuration/calendarInstance/:id', { id: device.calendarId }))}
                  >
                    {calendarName}
                  </Button>
                </span>
              </Tooltip>
            </Grid>
          </Grid>
        </DefaultAccordion>
        <DefaultAccordion title={t_('POSITION PROPERTIES')}>
          <Grid container direction='row' spacing={2} columns={{ xs: 1, sm: 4, md: 8, lg: 12 }}>
            <PositionCard position={device.position} onChange={onPositionChange} editMode={editMode} />
          </Grid>
        </DefaultAccordion>
        <DefaultAccordion title={t_('GEOMETRICAL PROPERTIES')}>
          <Grid container direction='row' spacing={2} columns={{ xs: 1, sm: 4, md: 8, lg: 12 }}>
            {device.parameterList.map(
              (parameter) => (
                <Grid item xs={2} key={'geometric-' + parameter.code}>
                  <Grid container>
                    {
                      parameter.code === 'TOOLHOLDER_STANDARD'
                        ? (
                          <TextField
                            key={parameter.code}
                            label={t_(parameter.name)}
                            fullWidth
                            InputLabelProps={{
                              shrink: true
                            }}
                            onChange={(event) => onParameterChange(event.target.value, parameter.name)}
                            select
                            value={parameter.value === null || toolholderStandards.length === 0 ? '' : parameter.value}
                            disabled={!editMode}
                          >
                            {
                              parameter.code === 'TOOLHOLDER_STANDARD'
                                ? [
                                  <MenuItem key='toolholderStandardNone' value=''>{t_('NONE')}</MenuItem>,
                                  toolholderStandards.map((toolholderStandard, index) => (
                                    <MenuItem key={'toolholderStandard' + index} value={toolholderStandard.value}>
                                      {toolholderStandard.value}
                                    </MenuItem>
                                  ))
                                  ]
                                : null
                            }
                          </TextField>
                          )
                        : <TextFieldWithUnit
                            key={parameter.code}
                            name={t_(parameter.name)}
                            value={parameter.value}
                            onValueChange={onParameterChange}
                            unitValue={parameter.unit !== null ? parameter.unit.code : ''}
                            unitOptions={parameter.unitType.availableUnits}
                            onUnitValueChange={onParameterUnitChange}
                            disabled={!editMode}
                            onParameterErrorStateChange={onParameterErrorStateChange}
                          />
                    }
                  </Grid>
                </Grid>
              ))}
            <Grid item xs={2}>
              <Grid container>
                <TextField
                  fullWidth
                  label={t_('Asset model file name')}
                  id='deviceModelFile'
                  value={device.modelFile ?? undefined}
                  onChange={(event) => {
                    setDevice({ ...device, modelFile: event.target.value })
                  }}
                  InputLabelProps={{
                    shrink: true
                  }}
                  disabled={!editMode}
                />
              </Grid>
            </Grid>
          </Grid>
        </DefaultAccordion>
        <DefaultAccordion title={t_('OEE BUFFER')}>
          <TextField
            label={t_('Availability')}
            type='number'
            value={device.oeeAvailability}
            onChange={(event) => {
              setDevice({ ...device, oeeAvailability: event.target.value })
            }}
            InputLabelProps={{
              shrink: true
            }}
            disabled={!editMode}
            InputProps={{
              readOnly: !editMode,
              endAdornment: <InputAdornment position='end'>%</InputAdornment>
            }}
            inputProps={{
              min: 0,
              max: 100
            }}
          />
        </DefaultAccordion>
        {
          device.deviceType.deviceTypeFunctions.length < 1
            ? null
            : (
              <DefaultAccordion title={t_('FUNCTION PROPERTIES')}>
                {device.deviceType.deviceTypeFunctions.map(
                  (deviceTypeFunction, index) => (
                    <React.Fragment key={'deviceTypeFunction' + index}>
                      <Grid container direction='row' spacing={3} columns={{ xs: 1, sm: 4, md: 8, lg: 12 }} sx={{ padding: '0.5em' }}>
                        <Grid item xs={12}>
                          <Typography sx={{ flex: '1 1 100%' }} component='div'>
                            {t_(deviceTypeFunction.function.name)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            select
                            id={deviceTypeFunction.function.id + '_protocol'}
                            label={t_('Protocol')}
                            value={
                              protocols.length === 0
                                ? 0
                                : device.deviceFunctionProtocols.find(
                                  (configuredProtocol) =>
                                    configuredProtocol.functionProtocol.function
                                      .id === deviceTypeFunction.function.id
                                ) &&
                                  device.deviceFunctionProtocols.find(
                                    (configuredProtocol) =>
                                      configuredProtocol.functionProtocol.function
                                        .id === deviceTypeFunction.function.id
                                  ).functionProtocol.protocol
                                  ? device.deviceFunctionProtocols.find(
                                    (configuredProtocol) =>
                                      configuredProtocol.functionProtocol.function
                                        .id === deviceTypeFunction.function.id
                                  ).functionProtocol.protocol.id
                                  : 0
                            }
                            onChange={
                              (event) => {
                                const distinctDeviceFunctionProtocols =
                                  device.deviceFunctionProtocols.filter(
                                    (pr) => {
                                      return pr.functionProtocol.function.id !==
                                        deviceTypeFunction.function.id
                                    }
                                  )

                                const protocol = protocols.find(
                                  (pr) => {
                                    return pr.id === event.target.value
                                  }
                                )
                                if (protocol !== undefined) {
                                  const configs = []
                                  protocol.protocolParameters.forEach(
                                    (pr) => {
                                      configs.push({
                                        protocolValue: '',
                                        protocol: pr,
                                        protocolParameter: pr,
                                        deviceTypeFunction,
                                        functionProtocol: {
                                          function: deviceTypeFunction.function,
                                          protocol: protocols.find(
                                            (pr) => pr.id === event.target.value
                                          )
                                        }
                                      })
                                    })
                                  if (event.target.value > 0) {
                                    const dfProtocol = {
                                      functionProtocol: {
                                        function: deviceTypeFunction.function,
                                        protocol: protocols.find(
                                          (pr) => pr.id === event.target.value
                                        ),
                                        functionProtocolConfigurations: configs
                                      }
                                    }

                                    distinctDeviceFunctionProtocols.push(dfProtocol)
                                  }
                                }
                                setDevice({
                                  ...device,
                                  deviceFunctionProtocols: distinctDeviceFunctionProtocols
                                })
                              }
                            }
                            disabled={!editMode || !isUserAuthorized('PROPERTIES_DEVICE_DRIVER')}
                          >
                            <MenuItem key={0} value={0}>
                              {t_('NONE')}
                            </MenuItem>
                            {
                              protocols.map((pr, index) => (
                                <MenuItem key={pr.id + index} value={pr.id}>
                                  {pr.name}
                                </MenuItem>
                              ))
                            }
                          </TextField>
                        </Grid>
                        {
                          device.deviceFunctionProtocols.find(
                            (configuredProtocol) =>
                              configuredProtocol.functionProtocol.function.id ===
                              deviceTypeFunction.function.id
                          )
                            ? (
                                device.deviceFunctionProtocols
                                  .find(
                                    (configuredProtocol) =>
                                      configuredProtocol.functionProtocol.function.id ===
                                    deviceTypeFunction.function.id
                                  )
                                  .functionProtocol.protocol.protocolParameters.map(
                                    (protocol, index) => (
                                      <Grid key={index} item xs={12} md={4}>
                                        <TextField
                                          id={
                                          'function_' +
                                          deviceTypeFunction.function.id +
                                          '_parameter_' +
                                          protocol.id
                                        }
                                          label={t_(protocol.parameter)}
                                          value={handleValue(deviceTypeFunction, protocol)}
                                          onChange={(event) => {
                                            const copyDevice = { ...device }
                                            if (copyDevice.deviceFunctionProtocols.find((deviceFunctionProtocol) => {
                                              return deviceFunctionProtocol.functionProtocol.function.id ===
                                              deviceTypeFunction.function.id
                                            })) {
                                              device.deviceFunctionProtocols.map((deviceFunctionProtocol) => {
                                                return deviceFunctionProtocol.functionProtocol.functionProtocolConfigurations.find(
                                                  (configured) => {
                                                    if (configured.protocolParameter.id ===
                                                    protocol.id && configured.functionProtocol.function.id === deviceTypeFunction.function.id) {
                                                      configured.protocolValue = event.target.value
                                                    }
                                                    return false
                                                  })
                                              })
                                            }
                                            setDevice({ ...device, copyDevice })
                                          }}
                                          disabled={!editMode || !isUserAuthorized('PROPERTIES_DEVICE_DRIVER')}
                                        />
                                      </Grid>
                                    )
                                  )
                              )
                            : (
                              <div />
                              )
                        }
                        <Grid item xs={12}>
                          <Divider spacing={2} />
                        </Grid>
                      </Grid>
                    </React.Fragment>
                  )
                )}
              </DefaultAccordion>
              )
        }
        <ButtonBackSave
          editControl={() => changeBetweenVisualizeModes(true)}
          backButtonAction={() => goBack('BACK')}
          saveButtonAction={() => saveDevice()}
          ref={backSaveButtonRef}
        />
      </div>
    )
  }

  if (device.id) {
    return printDevice()
  }

  return <CircularProgress />
}

export default DeviceConfiguration
